import { Observable, OperatorFunction, Subject, from, pipe } from 'rxjs';
import { filter, map, mergeWith, scan } from 'rxjs/operators';
import {
  UnknownAction,
  UnknownActionCreator,
  VoidPayload,
} from './internal/types';
import { defaultErrorSubject } from './internal/defaultErrorSubject';
import { ofType } from './operators/operators';
import { ActionCreator } from './types/ActionCreator';
import { ObservableState } from './observableState';

const wrapInArray = <T>(val: T | T[]): T[] =>
  Array.isArray(val) ? val : [val];

export type Reducer<State, Payload = VoidPayload> = (
  previousState: State,
  payload: Payload,
  namespace?: string
) => State;

/**
 * "Registered Reducer" with uniform action payload, for better type inference
 * than "RegisteredActionReducer".
 */
export type RegisteredUniformActionReducer<State, Payload> = // <br>
  Reducer<State, Payload> & {
    trigger: { actions: ActionCreator<Payload>[] };
  };

type RegisteredActionReducer<State, Payload = any> = Reducer<State, Payload> & {
  trigger: {
    actions: UnknownActionCreator[];
  };
};
export type RegisteredStreamReducer<State, Payload = any> = Reducer<
  State,
  Payload
> & {
  trigger: {
    source$: Observable<Payload>;
  };
};

export type RegisteredReducer<State, Payload = any> = Reducer<
  State,
  Payload
> & {
  trigger:
    | {
        actions: UnknownActionCreator[];
      }
    | {
        source$: Observable<Payload>;
      };
};

export type ReducerArray<State, Payload = any> = RegisteredReducer<
  State,
  Payload
>[];

const isActionReducer = <State, Payload>(
  reducerFn: RegisteredReducer<State, Payload>
): reducerFn is RegisteredActionReducer<State, Payload> =>
  'actions' in reducerFn.trigger;

const isStreamReducer = <State, Payload>(
  reducerFn: RegisteredReducer<State, Payload>
): reducerFn is RegisteredStreamReducer<State, Payload> =>
  'source$' in reducerFn.trigger;

/**
 * Connect single- or multi-action triggers to a reducer.
 * Should not be exported since we don't want to expose a reducer function that
 * is polymorphic on the action trigger. Instead, use:
 * - `actionReducer` for single-action reducers
 * - `multiActionReducer` for multi-action reducers
 */
const connectActionTrigger = <State, Payload>(
  trigger: ActionCreator<Payload> | ActionCreator<Payload>[],
  reducerFn: Reducer<State, Payload>
): RegisteredUniformActionReducer<State, Payload> => {
  const wrapper = (state: State, payload: any, namespace?: string) =>
    reducerFn(state, payload, namespace);

  wrapper.trigger = { actions: wrapInArray(trigger) };

  return wrapper;
};

/**
 * Connect a stream to a reducer
 */
export const streamReducer = <State, Payload>(
  trigger: Observable<Payload> | ObservableState<Payload>,
  reducerFn: Reducer<State, Payload>
): RegisteredStreamReducer<State, Payload> => {
  const wrapper = (state: State, payload: any, namespace?: string) =>
    reducerFn(state, payload, namespace);

  wrapper.trigger = { source$: from(trigger) };

  return wrapper;
};

/**
 * This type achieves the following:
 * - Allows any action creator to be paired with a reducer that doesn't use the
 *   second (payload) parameter.
 * - Improves error specificity from the whole reducer()-composition to the
 *   action creator by making TS infer the Payload from the reducer and forcing
 *   the action creator to match.
 */
type RelaxedActionReducerCreator<Payload> = [Payload] extends [void]
  ? ActionCreator<any>
  : ActionCreator<Payload>;

/**
 * Connect a single action to a reducer
 */
export const actionReducer = <State, Payload = void>(
  trigger: RelaxedActionReducerCreator<Payload>,
  reducerFn: Reducer<State, Payload>
): RegisteredUniformActionReducer<State, Payload> => {
  return connectActionTrigger<State, Payload>(trigger, reducerFn);
};

type ActionReducerCreator = typeof actionReducer;

/**
 * Alias for actionReducer
 */
export const reducer: ActionReducerCreator = actionReducer;

/**
 * Connect a reducer function to multiple actions with the same payload
 */
export const multiActionReducer = <State, Payload>(
  trigger: ActionCreator<Payload>[],
  reducerFn: Reducer<State, Payload>
): RegisteredUniformActionReducer<State, Payload> => {
  return connectActionTrigger(trigger, reducerFn);
};

const ACTION_ORIGIN = Symbol('Action origin');

type CombineReducersConfig = {
  errorSubject?: Subject<any>;
  namespace?: string;
};

/**
 * Combine registered reducers into a stream operator
 *
 * Each reducer will receive the previous state (or the seed if it's the first
 * invocation) together with the payloads of the actions of the given reducer,
 * or the emitted values from the stream of the given reducer.
 *
 * The behaviour is undefined if multiple reducers are registered for the same
 * actions.
 *
 * This operator does not change whether the stream is hot or cold.
 *
 * The order of invocation for the reducers is controlled by the rxjs operator
 * `merge`, which is called with all the actions first and then the source
 * streams in the order their reducers are defined in the `reducers` argument.
 *
 * If a reducer throws an error, it will be nexted on the error subject. If the
 * error subject is not explicitly set, it will default to
 * `defaultErrorSubject`, which will rethrow the errors globally, as uncaught
 * exceptions. The stream will not complete or emit any value upon an error.
 *
 * @param seed The initial input to the first reducer call
 * @param reducers The reducer entries that should be combined
 * @param namespace Namespace to pass on to the reducers. Note that this will
 *                  always be passed, regardless of namespaces of the actions.
 * @see rxjs.merge
 */
export const combineReducers = <State>(
  seed: State,
  reducers: RegisteredReducer<State, any>[],
  { errorSubject = defaultErrorSubject, namespace }: CombineReducersConfig = {}
): OperatorFunction<UnknownAction, State> => {
  const actionReducers = reducers.filter(isActionReducer);
  const streamReducers = reducers.filter(isStreamReducer);
  const reducersByActionType = new Map(
    actionReducers.flatMap(reducerFn =>
      reducerFn.trigger.actions.map(actionCreator => [
        actionCreator.type,
        reducerFn,
      ])
    )
  );

  type Packet =
    | { origin: typeof ACTION_ORIGIN; value: UnknownAction }
    | { origin: number; value: any };

  const source$s = streamReducers.map((reducerFn, i) =>
    reducerFn.trigger.source$.pipe(
      map((payload): Packet => ({ origin: i, value: payload }))
    )
  );

  return pipe(
    ofType(...actionReducers.flatMap(reducerFn => reducerFn.trigger.actions)),
    map((action): Packet => ({ origin: ACTION_ORIGIN, value: action })),
    mergeWith(...source$s),
    scan(
      ({ state }, packet) => {
        try {
          if (packet.origin === ACTION_ORIGIN) {
            const reducerFn = reducersByActionType.get(packet.value.type)!;
            return {
              caughtError: false,
              state: reducerFn(state, packet.value.payload, namespace),
            };
          }

          const reducerFn = streamReducers[packet.origin]!;
          return {
            caughtError: false,
            state: reducerFn(state, packet.value, namespace),
          };
        } catch (e) {
          errorSubject.next(e);
          return {
            caughtError: true,
            state,
          };
        }
      },
      { state: seed, caughtError: false }
    ),
    filter(({ caughtError }) => caughtError === false),
    map(({ state }) => state)
  );
};
